import { useCallback, useMemo } from 'react';
import { useRouter } from '@sls-react/routing';
import qsUpdateString from '@sls-react/utils/qsUpdateString';
import extractPathname from '@sls-react/utils/extractPathname';
import {
  RailsItem,
  RailsThumbnailItem,
} from '@sls-react/typings/services/rails/item';
import { RailsCartItemItem } from '@sls-react/typings/services/rails/cart';
import { AnalyticsSource } from '@sls-react/services/analytics/AnalyticsInterfaces';

export interface ItemDetailsLinkParams {
  item?: RailsItem | RailsThumbnailItem | RailsCartItemItem;
  itemId?: number | string;
  src?: AnalyticsSource;
  url?: string;

  // Use this if you want to open the Published modal on the destination page
  justPublished?: boolean;
}

export default function useItemDetailsLink({
  item,
  itemId,
  justPublished,
  src,
  url,
}: ItemDetailsLinkParams) {
  const { locale } = useRouter();

  return useMemo(() => {
    const query = qsUpdateString([
      url?.split('?')[1],
      {
        src,
        published: justPublished ? 1 : undefined,
      },
    ]);

    let itemUrl: string | undefined;

    if (item?.state === 'draft') {
      itemUrl = `/items/${item.id}`;
    } else if (url) {
      if (url.startsWith('http')) {
        itemUrl = extractPathname(url) + query;
      } else {
        itemUrl = url + query;
      }
    } else if (item?.url) {
      itemUrl = extractPathname(item.url) + query;
    } else if (item?.id || itemId) {
      itemUrl = `/gear/${item?.id || itemId}` + query;
    }

    if (!itemUrl) {
      throw new Error('No URL-identifiable property defined');
    }

    if (locale !== 'en-US') {
      itemUrl = `/${locale}${itemUrl}`;
    }

    return itemUrl;
  }, [item?.id, item?.state, item?.url, itemId, locale, src, url]);
}
